// Sound → appearance rules. Each creature's look follows its sound:
//  - longer samples → bigger creature           (size)
//  - higher pitch   → taller and slimmer, lower → shorter and heavier   (stature)
//  - more complex waveform → more colourful (extra colours, patterns)    (colorfulness)
//  - sharp, punchy attacks → spikier (horns, teeth, pointy ears)         (spikiness)
//  - noisy sounds  → fuzzier (fur tufts, fluffy outline)                 (fuzz)
//  - bright sounds → bigger, sparklier eyes                             (brightness)
//  - long decays   → longer tails / tendrils                            (tail)
//  - wide stereo   → wider wings / ears / arm span                      (width)
//  - reverb + tonal sustain → glow / aura                               (glow)
//  - sustained lows → jelly wobble                                      (wobble)
// Pure functions, no Pixi.
import type { Analysis } from "@eb/shared";
import type { Pad } from "@eb/shared";
import type { SynthParams } from "@eb/shared";

export interface CreatureTraits {
  /** Overall size multiplier, 0.78–1.32. */
  size: number;
  /** −1 short & heavy … +1 tall & slim. */
  stature: number;
  /** 0–1. */
  colorfulness: number;
  spikiness: number;
  fuzz: number;
  brightness: number;
  tail: number;
  width: number;
  glow: number;
  wobble: number;
}

export const NEUTRAL_TRAITS: CreatureTraits = {
  size: 1,
  stature: 0,
  colorfulness: 0.3,
  spikiness: 0.3,
  fuzz: 0.2,
  brightness: 0.5,
  tail: 0.4,
  width: 0.3,
  glow: 0.2,
  wobble: 0.2,
};

const clamp = (v: number, lo = 0, hi = 1) => (v < lo ? lo : v > hi ? hi : v);
/** Map v from [a,b] on a log scale to [0,1]. */
const logUnit = (v: number, a: number, b: number) => clamp(Math.log(Math.max(v, 1e-9) / a) / Math.log(b / a));

/** Traits for a sample pad. `a` may be null while the sample is still loading. */
export function traitsFromSound(a: Analysis | null | undefined, pad: Pad): CreatureTraits {
  if (!a) return { ...NEUTRAL_TRAITS };
  const pitchRatio = Math.pow(2, (pad.tune + pad.fine / 100) / 12);
  const region = clamp(pad.end - pad.start, 0.01, 1);
  const duration = (a.duration_s * region) / pitchRatio;
  // Effective pitch: the detected fundamental, or a centroid-based guess for unpitched sounds.
  const f0 = (a.pitch_hz > 0 ? a.pitch_hz : a.centroid_hz / 6) * pitchRatio;
  return {
    size: 0.78 + 0.54 * logUnit(duration, 0.06, 3),
    stature: clamp(Math.log2(Math.max(f0, 20) / 220) / 3, -1, 1),
    colorfulness: clamp(a.complexity * 1.5),
    spikiness: clamp(((a.crest_db - 6) / 14) * (a.attack_ms < 8 ? 1 : 0.4)),
    fuzz: clamp((1 - a.pitchedness) * Math.min(1, a.complexity * 2.2)),
    brightness: logUnit(a.centroid_hz, 150, 12000),
    tail: logUnit(a.decay_s, 0.05, 2.5),
    width: clamp(Math.max(a.stereo_width, Math.abs(pad.pan) * 0.6)),
    glow: clamp(Math.max(pad.reverb_send * 0.9, a.pitchedness * (a.decay_s > 0.4 ? 0.6 : 0.2))),
    wobble: clamp(a.low_ratio * Math.min(1, a.decay_s)),
  };
}

/** Traits for a synth pad, from its parameters (note, filter, envelope…). */
export function traitsFromSynth(s: SynthParams, pad: Pad): CreatureTraits {
  const note = s.note + pad.tune + pad.fine / 100;
  const sustain = pad.envelope.sustain * 1.2 + pad.envelope.release_ms / 1500 + s.env_decay / 3000;
  return {
    size: 0.82 + 0.45 * clamp(sustain / 1.8),
    stature: clamp((note - 57) / 30, -1, 1),
    colorfulness: clamp(0.25 + s.tone * 0.45 + s.detune * 0.3),
    spikiness: clamp(s.resonance * 0.8 + s.drive * 0.3),
    fuzz: clamp(s.drive * 0.8),
    brightness: clamp(s.cutoff),
    tail: clamp(s.glide_ms / 400 + s.env_decay / 2000),
    width: clamp(s.detune),
    glow: clamp(Math.max(pad.reverb_send, s.model === "organ" || s.model === "fm" ? 0.35 : 0.15)),
    wobble: clamp(s.glide_ms / 300 + (s.model === "acid" ? 0.3 : 0)),
  };
}

export function traitsForPad(pad: Pad, a: Analysis | null | undefined): CreatureTraits {
  return pad.synth ? traitsFromSynth(pad.synth, pad) : traitsFromSound(a, pad);
}
